import { z } from 'zod';
import { ORDER_STATUSES } from '../constants/order-states.js';
import { emailSchema, moneyCentsSchema, usPhoneSchema, uuidSchema } from './common.js';
import { unitAttributesSchema } from './inventory.js';
import { cartInputSchema, checkoutSchema, closedNoticeSchema } from './shop.js';
import { SERVICE_PLAN_BILLING_INTERVALS } from './subscription.js';
/**
 * The in-store kiosk's own API surface (`/kiosk-terminal/*`, t1077). The
 * browser kiosk pairs like any other kiosk device, then asks for texted
 * sign-in codes, sells a cart over Stripe Elements, lists the customer's
 * devices, and runs plan subscriptions / bill payments.
 */
// ------------------------------------------------------------ device + config
export const kioskTerminalConfigSchema = z.object({
    /** The inventory location this kiosk sits in — lets the screen tell "on the
     *  shelf here" from "we can bring it over". */
    locationId: uuidSchema,
    locationName: z.string(),
    cardCheckoutEnabled: z.boolean(),
    /** Needed by Stripe.js; null while card payment is not set up. */
    stripePublishableKey: z.string().nullable(),
    /** False while texts cannot be sent (no SMS provider, or still pending
     *  carrier approval) — the kiosk hides sign-in rather than offering a code
     *  that will never arrive. */
    smsSignInAvailable: z.boolean(),
    /** t1130: set while the store is closed for Shabbos or Yom Tov. */
    closed: closedNoticeSchema.nullable().optional(),
});
export const kioskTerminalHeartbeatSchema = z.object({
    agentVersion: z.string().trim().max(40).optional(),
});
export const kioskTerminalHeartbeatResultSchema = z.object({
    ok: z.literal(true),
    /** Set when the kiosk's device token is close to expiring: store this one
     *  in its place. The old token keeps working for a day. */
    newToken: z.string().nullable(),
    /** t1130: set while the store is closed for Shabbos or Yom Tov — the kiosk
     *  shows a closed screen until a heartbeat comes back without it. */
    closed: closedNoticeSchema.nullable().optional(),
});
// ------------------------------------------------------------------- sign-in
export const kioskOtpSendSchema = z.object({ phone: usPhoneSchema });
export const KIOSK_OTP_UNSENT_REASONS = ['too_many_codes', 'sms_unavailable'];
export const kioskOtpSendResultSchema = z.object({
    sent: z.boolean(),
    reason: z.enum(KIOSK_OTP_UNSENT_REASONS).nullable(),
});
export const kioskOtpVerifySchema = z.object({
    phone: usPhoneSchema,
    code: z
        .string()
        .trim()
        .regex(/^\d{6}$/, 'Enter the 6-digit code'),
});
export const kioskSessionSchema = z.object({
    customerSessionToken: z.string(),
    /** True for an account created a moment ago that has no name yet. */
    needsName: z.boolean(),
    customer: z.object({
        id: uuidSchema,
        name: z.string(),
        email: z.string().nullable(),
        phone: z.string().nullable(),
    }),
});
export const kioskCompleteProfileSchema = z.object({
    name: z.string().trim().min(1).max(150),
});
// ------------------------------------------------------------------ checkout
export const kioskCheckoutSchema = z.object({
    items: z
        .array(z.object({
        productId: uuidSchema,
        qty: z.number().int().min(1).max(10),
    }))
        .min(1)
        .max(20),
    /** Guests may leave these empty; a signed-in customer's are already known. */
    buyerName: z.string().trim().max(150).nullable().default(null),
    buyerPhone: usPhoneSchema.nullable().default(null),
    idempotencyKey: z.string().trim().min(8).max(120),
    /** Gift cards typed in at the kiosk. Never an amount. */
    giftCardCodes: z.array(z.string().trim().min(1).max(64)).max(3).default([]),
    /** Spend a signed-in customer's store credit; refused for a guest. */
    useStoreCredit: z.boolean().default(false),
});
/**
 * Delivery / store-pickup checkout at the kiosk (t1103): the website's own
 * checkout, so pricing, sourcing and pickup/shipping rules are the shop's.
 * Card only. Notes and repair drop-offs do not apply at a kiosk, and promo
 * codes are a website feature, so none of those are accepted.
 *
 * A guest (t1306) may order store pickup without signing in, but only with a
 * phone number so the store can reach them.
 */
export const kioskShopCheckoutSchema = checkoutSchema
    .omit({
    paymentMethod: true,
    note: true,
    repairDropOffDate: true,
    repairDropOffLocationId: true,
    couponCode: true,
})
    .extend({
    /** A guest's number for a pickup order; ignored for a signed-in customer. */
    buyerPhone: usPhoneSchema.nullable().default(null),
});
/** The kiosk's cart quote: the website's, minus promo codes. */
export const kioskCartPriceSchema = cartInputSchema.omit({ couponCode: true });
export const kioskCheckoutResultSchema = z.object({
    orderId: uuidSchema,
    orderNumber: z.string(),
    totalCents: z.number().int(),
    /** Null when gift card / store credit covered the whole order — it is
     *  already paid and there is no card to take. */
    clientSecret: z.string().nullable(),
    creditAppliedCents: z.number().int().default(0),
    /** What the card is charged: total minus credit. */
    dueCents: z.number().int().default(0),
});
export const kioskOrderStatusSchema = z.object({
    orderId: uuidSchema,
    orderNumber: z.string(),
    status: z.enum(ORDER_STATUSES),
    payment: z.enum(['pending', 'paid', 'failed']),
    totalCents: z.number().int(),
    /** Gift cards this order bought, shown once on the done screen and then
     *  never again — the full code is not kept after it has been delivered. */
    giftCardCodes: z.array(z.object({ code: z.string(), amountCents: z.number().int() })).default([]),
});
// ------------------------------------------------------------------- devices
export const kioskDeviceListItemSchema = z.object({
    id: uuidSchema,
    imei: z.string(),
    productName: z.string(),
    sku: z.string(),
    status: z.string(),
    orderId: uuidSchema.nullable(),
    orderNumber: z.string().nullable(),
    soldAt: z.iso.datetime().nullable(),
    attributes: unitAttributesSchema,
});
// ------------------------------------------------------- plans + subscriptions
export const kioskPlanSchema = z.object({
    servicePlanId: uuidSchema,
    productId: uuidSchema,
    name: z.string(),
    monthlyCents: moneyCentsSchema,
    billingInterval: z.enum(SERVICE_PLAN_BILLING_INTERVALS),
    includedMinutes: z.number().int().nullable(),
    includedTexts: z.number().int().nullable(),
});
export const kioskSubscriptionSchema = z.object({
    id: uuidSchema,
    planName: z.string(),
    status: z.enum(['active', 'past_due', 'paused', 'cancelled', 'incomplete']),
    phoneLine: z.string().nullable(),
    monthlyCents: moneyCentsSchema,
    billingInterval: z.enum(SERVICE_PLAN_BILLING_INTERVALS),
    currentPeriodEnd: z.iso.datetime().nullable(),
});
/** What a customer types at checkout when buying a plan without signing in
 *  (t1395). The server keeps a password-less customer record from it. */
export const planGuestSchema = z.object({
    name: z.string().trim().min(1).max(150),
    phone: usPhoneSchema,
    email: emailSchema.nullable().default(null),
});
export const kioskStartSubscriptionSchema = z.object({
    servicePlanId: uuidSchema,
    phoneLine: usPhoneSchema.nullable().default(null),
    /** Required when no customer is signed in; ignored when one is. */
    guest: planGuestSchema.nullable().default(null),
});
export const kioskStartSubscriptionResultSchema = z.object({
    subscriptionId: uuidSchema,
    amountDueCents: z.number().int(),
    clientSecret: z.string(),
    /** Set when a guest's details were turned into a customer record: the
     *  session that lets the terminal follow the new plan to "paid". */
    guestSession: kioskSessionSchema.nullable().optional(),
});
/** Whole dollars from $5 to $200: a top-up is a round amount at a kiosk. */
export const KIOSK_TOPUP_MIN_CENTS = 500;
export const KIOSK_TOPUP_MAX_CENTS = 20_000;
export const kioskTopUpSchema = z.object({
    amountCents: z
        .number()
        .int()
        .min(KIOSK_TOPUP_MIN_CENTS)
        .max(KIOSK_TOPUP_MAX_CENTS)
        .refine((v) => v % 100 === 0, 'Top-ups are in whole dollars'),
});
export const kioskPayableResultSchema = z.object({
    amountDueCents: z.number().int(),
    clientSecret: z.string(),
});
export const KIOSK_BILL_KINDS = ['invoice', 'order'];
export const kioskBillSchema = z.object({
    kind: z.enum(KIOSK_BILL_KINDS),
    /** Stripe invoice id, or the order id — echoed back to pay it. */
    ref: z.string(),
    label: z.string(),
    amountDueCents: z.number().int(),
});
export const kioskPayBillSchema = z.object({
    kind: z.enum(KIOSK_BILL_KINDS),
    ref: z.string().min(1).max(120),
});
// ── Rental return drop-off (t1105) ─────────────────────────────────────────
// The customer scans the barcode on the item they are bringing back; nothing
// is recorded until they confirm. Walk-up: device token only, no customer
// session, so the lookup reveals a first name and counts and nothing else.
export const kioskReturnLookupSchema = z.object({ code: z.string().trim().min(3).max(64) });
export const kioskReturnLookupResultSchema = z.discriminatedUnion('found', [
    z.object({
        found: z.literal(true),
        rentalId: uuidSchema,
        rentalNumber: z.string(),
        firstName: z.string().nullable(),
        itemCount: z.number().int().min(1),
        alreadyAnnounced: z.boolean(),
    }),
    z.object({ found: z.literal(false), message: z.string() }),
]);
export const kioskReturnAnnounceSchema = z.object({ rentalId: uuidSchema });
export const kioskReturnAnnounceResultSchema = z.object({
    ok: z.literal(true),
    rentalNumber: z.string(),
});
// ── Saved cart (t1399) ─────────────────────────────────────────────────────
// A signed-in customer's kiosk cart, kept on their account. Ids and quantities
// go up; the server answers with today's name and price, never a stored one.
export const kioskSavedCartInputSchema = z.object({
    lines: z.array(z.object({ productId: uuidSchema, qty: z.number().int().min(1).max(10) })).max(50),
});
export const kioskSavedCartSchema = z.object({
    lines: z.array(z.object({
        productId: uuidSchema,
        sku: z.string(),
        name: z.string(),
        unitPriceCents: z.number().int(),
        imageUrl: z.string().nullable(),
        isGiftCard: z.boolean(),
        qty: z.number().int().min(1),
    })),
});
//# sourceMappingURL=kiosk-terminal.js.map